import React, { useState, useMemo } from 'react';
import moment from 'moment';
import { Search, X, Filter } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const MissingStocksTable = ({ data, loading }) => {
  const [symbolSearch, setSymbolSearch] = useState('');
  const [indexFilter, setIndexFilter] = useState('ALL');
  const [countryFilter, setCountryFilter] = useState('ALL');

  const { filteredData, uniqueIndices, uniqueCountries } = useMemo(() => {
    if (!data) return { filteredData: [], uniqueIndices: [], uniqueCountries: [] };

    const indices = new Set();
    const countries = new Set();
    data.forEach(item => {
      if (item.index_name) indices.add(item.index_name);
      if (item.country) countries.add(item.country);
    });

    let filtered = data;
    if (symbolSearch) {
      const search = symbolSearch.toLowerCase();
      filtered = filtered.filter(item => item.symbol.toLowerCase().includes(search));
    }
    if (indexFilter !== 'ALL') {
      filtered = filtered.filter(item => item.index_name === indexFilter);
    }
    if (countryFilter !== 'ALL') {
      filtered = filtered.filter(item => item.country === countryFilter);
    }

    return {
      filteredData: filtered,
      uniqueIndices: Array.from(indices).sort(),
      uniqueCountries: Array.from(countries).sort()
    };
  }, [data, symbolSearch, indexFilter, countryFilter]);

  const clearFilters = () => {
    setSymbolSearch('');
    setIndexFilter('ALL');
    setCountryFilter('ALL');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 gap-4">
        <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
        <span className="text-zinc-500 font-black text-sm uppercase tracking-widest animate-pulse">Fetching missing stocks...</span>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-20 text-zinc-500">
        <div className="bg-emerald-500/10 p-4 rounded-full mb-4">
          <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <span className="font-black text-base uppercase tracking-widest text-emerald-500/80">All stocks synced successfully</span>
        <p className="text-[12px] mt-2 font-bold opacity-50 uppercase">No missing stock records found in database</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 bg-zinc-900/40 p-3 rounded-xl border border-zinc-900/50">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input 
            placeholder="Search symbol..." 
            value={symbolSearch}
            onChange={(e) => setSymbolSearch(e.target.value)}
            className="pl-10 bg-zinc-950/50 border-zinc-800 text-[14px] h-9 text-white placeholder:text-zinc-500"
          />
          {symbolSearch && (
            <button 
              onClick={() => setSymbolSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        <select 
          value={indexFilter}
          onChange={(e) => setIndexFilter(e.target.value)}
          className="bg-zinc-950 border border-zinc-800 text-white text-[14px] h-9 px-3 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500/50 flex-1 min-w-[150px] cursor-pointer appearance-none"
          style={{ backgroundImage: 'linear-gradient(45deg, transparent 50%, #71717a 50%), linear-gradient(135deg, #71717a 50%, transparent 50%)', backgroundPosition: 'calc(100% - 15px) calc(1em + 2px), calc(100% - 10px) calc(1em + 2px)', backgroundSize: '5px 5px, 5px 5px', backgroundRepeat: 'no-repeat' }}
        >
          <option value="ALL" className="bg-zinc-950 text-white">All Indices</option>
          {uniqueIndices.map(idx => (
            <option key={idx} value={idx} className="bg-zinc-950 text-white">{idx}</option>
          ))}
        </select>

        <select 
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          className="bg-zinc-950 border border-zinc-800 text-white text-[14px] h-9 px-3 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500/50 flex-1 min-w-[150px] cursor-pointer appearance-none"
          style={{ backgroundImage: 'linear-gradient(45deg, transparent 50%, #71717a 50%), linear-gradient(135deg, #71717a 50%, transparent 50%)', backgroundPosition: 'calc(100% - 15px) calc(1em + 2px), calc(100% - 10px) calc(1em + 2px)', backgroundSize: '5px 5px, 5px 5px', backgroundRepeat: 'no-repeat' }}
        >
          <option value="ALL" className="bg-zinc-950 text-white">All Countries</option>
          {uniqueCountries.map(c => (
            <option key={c} value={c} className="bg-zinc-950 text-white">{c}</option>
          ))}
        </select>

        {(symbolSearch || indexFilter !== 'ALL' || countryFilter !== 'ALL') && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters}
            className="h-9 px-3 text-zinc-500 hover:text-white hover:bg-zinc-800"
          >
            Clear
          </Button>
        )}
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-zinc-900 shadow-inner max-h-[60vh] custom-scroll">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-20">
            <tr className="bg-zinc-900/90 backdrop-blur-md border-b border-zinc-800 shadow-sm">
              <th className="px-4 py-3 text-left text-zinc-500 font-black uppercase tracking-wider w-10">#</th>
              <th className="px-4 py-3 text-left text-zinc-500 font-black uppercase tracking-wider">Symbol</th>
              <th className="px-4 py-3 text-left text-zinc-500 font-black uppercase tracking-wider">Index</th>
              <th className="px-4 py-3 text-left text-zinc-500 font-black uppercase tracking-wider">Country</th>
              <th className="px-4 py-3 text-left text-zinc-500 font-black uppercase tracking-wider">Reason</th>
              <th className="px-4 py-3 text-right text-zinc-500 font-black uppercase tracking-wider">First Seen</th>
              <th className="px-4 py-3 text-right text-zinc-500 font-black uppercase tracking-wider">Last Seen</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? filteredData.map((row, i) => (
              <tr key={`${row.symbol}-${row.index_name}-${i}`} className="border-b border-zinc-900 hover:bg-zinc-900/30 transition-colors group">
                <td className="px-4 py-3 text-zinc-600 font-mono text-[12px] group-hover:text-emerald-500/50">{i + 1}</td>
                <td className="px-4 py-3 font-black text-blue-400 uppercase tracking-tight">{row.symbol}</td>
                <td className="px-4 py-3 text-zinc-300 font-bold">{row.index_name}</td>
                <td className="px-4 py-3 text-zinc-400 font-bold uppercase text-[12px]">{row.country}</td>
                <td className="px-4 py-3">
                  <span className="bg-rose-500/10 text-rose-500 px-2 py-0.5 rounded border border-rose-500/20 font-black text-[12px] uppercase">
                    {row.missing_reason}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-zinc-500 font-mono">
                  {moment(row.first_seen_at).format('DD MMM HH:mm')}
                </td>
                <td className="px-4 py-3 text-right text-zinc-200 font-mono font-bold">
                  {moment(row.last_seen_at).format('DD MMM HH:mm')}
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="7" className="p-20 text-center text-zinc-600 font-black uppercase text-[13px] tracking-widest bg-zinc-900/20">
                  No matching failures found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="px-2 py-1 flex justify-between items-center text-[12px] font-bold text-zinc-600 uppercase tracking-widest">
        <span>Showing {filteredData.length} of {data.length} records</span>
        {filteredData.length !== data.length && <span>Filters active</span>}
      </div>
    </div>
  );
};

export default MissingStocksTable;
